// ChatbotWidget.updated.jsx
import React from "react";
import realBotImage from "./female.png";
import animatedBotGif from "./video2.gif";
import logo from "./logo.png";
import "./ChatPopup.scss";

const BACKEND_URL = "https://voicebot-pe.outworx.com/api"; // expects /stt and /tts endpoints (see notes)
const WAKE_WORDS = ["parky", "hey parky", "hello", "hi parky"];

export default class ChatbotWidget extends React.Component {
  constructor(props) {
    super(props);
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(navigator.platform) ||
      (ua.includes("Mac") && "ontouchend" in document);

    this.state = {
      awake: false,
      listening: false,
      speaking: false,
      conversation: [],
      currentLang: localStorage.getItem("preferredLang") || "en-US",
      isProcessing: false,
      message: "",
      typingDone: false,
       micLocked: false,   // ← NEW
    };

    // Feature flags
    this.isIOS = isIOS;
    this.hasBrowserSTT = !!(window.SpeechRecognition || window.webkitSpeechRecognition) && !this.isIOS;
    this.hasBrowserTTS = !!window.speechSynthesis && !this.isIOS;

    // legacy fields
    this.recognition = null;
    this.mediaRecorder = null;
    this.audioStream = null;
    this.silenceTimer = null;
    this.sessionId = crypto.randomUUID();
    this.conversationRef = React.createRef();
    this.streamingUserText = "";
    this._voicesLoaded = false;
    this._preferredVoiceName = localStorage.getItem("preferredFemaleVoice") || null;
    this._firstUserGestureDone = false;
    this._iosRecordingTimeslice = 1500; // ms — chunk size sent to backend
    this._iosRecordingActive = false;
    

    // === NEW: short silence buffer for robust finalization on Android/browser STT ===
    // collects interim/final text until 2s of silence
    this.activeBuffer = "";
    this.shortSilenceTimer = null;
  }

  async componentDidMount() {
    await this.preloadVoices();

    if (this.hasBrowserSTT) this.initSpeechRecognition();
    else if (this.isIOS) await this.initIOSMedia();

    this.introTyping();

    const gestureHandler = () => {
      this._firstUserGestureDone = true;
      this.preloadVoices();
      window.removeEventListener("pointerdown", gestureHandler);
      window.removeEventListener("touchstart", gestureHandler);
    };
    window.addEventListener("pointerdown", gestureHandler);
    window.addEventListener("touchstart", gestureHandler);
  }

  introTyping() {
    const introMsg = "  ";
    let i = 0;
    const interval = setInterval(() => {
      if (i < introMsg.length) {
        this.setState((p) => ({ message: p.message + introMsg[i] }));
        i++;
      } else clearInterval(interval);
    }, 45);
  }

  // -------------------- SpeechRecognition (desktop / Android / macOS) --------------------
initSpeechRecognition() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    console.warn("SpeechRecognition not supported in this browser.");
    return;
  }

  this.recognition = new SR();
  this.recognition.continuous = true;
  this.recognition.interimResults = true;
  this.recognition.lang = this.state.currentLang;
  

  // -------------------- REPLACED onresult: buffer + 2s silence finalization --------------------
  if (this.state.micLocked) return;  // Ignore noise while processing/tts
  this.recognition.onresult = (event) => {
    let interimTranscript = "";
    let finalTranscript = "";

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result.isFinal) finalTranscript += result[0].transcript;
      else interimTranscript += result[0].transcript;
    }

    const finalText = finalTranscript.trim();
    const interimText = interimTranscript.trim();

    // Stream only for UI display (unchanged)
    this.updateStreamingUser(interimText || finalText);

    // Wake-word check only on real final text (unchanged behavior)
    if (!this.state.awake && finalText && WAKE_WORDS.some((w) => finalText.toLowerCase().includes(w))) {
      this.wakeUpAssistant();
      this.activeBuffer = "";
      this._clearShortSilenceTimer();
      return;
    }

    // If not awake yet → ignore
    if (!this.state.awake) return;

    // Reset long silence timer (sleep after 1 minute) if actually speaking
    if (interimText || finalText) this.resetSilenceTimer();

    // === NEW BUFFERING LOGIC (use silence to decide final) ===
    // Keep latest transcript (prefer final > interim) in buffer
    if (finalText) this.activeBuffer = finalText;
    else if (interimText) this.activeBuffer = interimText;

    // Restart 2s short-silence timer — when it fires we treat buffer as final
    this._resetShortSilenceTimer();
  };

  this.recognition.onerror = (e) => {
    console.warn("SpeechRecognition error:", e);
    this.setState({ listening: false });
  };

this.recognition.onend = () => {
  if (this.state.awake && !this.state.speaking && !this.state.micLocked) {
      try {
       this.recognition.start();
      this.setState({ listening: true });
      } catch (e) {
        console.warn("Restart error:", e);
      }
    }
  };
}


  // -------------------- iOS MediaRecorder-based STT (send chunks to backend) --------------------
  async initIOSMedia() {
    try {
      // request mic permission early (user gesture may be required to get audio in Safari)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // we don't keep it open by default; store for later start/stop
      this.audioStream = stream;
    } catch (err) {
      console.warn("iOS: getUserMedia failed:", err);
      this.audioStream = null;
    }
  }

  startIOSRecording() {
    if (!this.audioStream) {
      console.warn("No audio stream available on iOS. Trying to request permission now.");
      // attempt to request again — user gesture may be required
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((s) => {
          this.audioStream = s;
          this._startMediaRecorderInternal();
        })
        .catch((e) => console.warn("getUserMedia denied:", e));
      return;
    }

    this._startMediaRecorderInternal();
  }

  _startMediaRecorderInternal() {
    if (this.mediaRecorder && this._iosRecordingActive) return;

    try {
      const options = {};
      // Many browsers support webm/opus; backend should accept common audio blobs
      this.mediaRecorder = new MediaRecorder(this.audioStream, options);
      this.mediaRecorder.ondataavailable = async (ev) => {
        if (ev.data && ev.data.size > 0) await this._sendIOSChunk(ev.data);
      };
      this.mediaRecorder.onerror = (e) => console.warn("MediaRecorder error:", e);
      this.mediaRecorder.start(this._iosRecordingTimeslice);
      this._iosRecordingActive = true;
      this.setState({ listening: true });
      console.log("📡 iOS MediaRecorder started");
    } catch (err) {
      console.warn("Failed to start MediaRecorder:", err);
    }
  }

  stopIOSRecording() {
    if (this.mediaRecorder && this._iosRecordingActive) {
      try {
        this.mediaRecorder.stop();
      } catch (e) {}
      this._iosRecordingActive = false;
      this.setState({ listening: false });
      console.log("📴 iOS MediaRecorder stopped");
    }
  }

  async _sendIOSChunk(blob) {
    // Send a short audio blob to backend STT endpoint
    try {
      const fd = new FormData();
      fd.append("audio", blob, "chunk.webm");
      fd.append("session_id", this.sessionId);
      fd.append("lang", this.state.currentLang);

      const res = await fetch(`${BACKEND_URL}/stt`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        console.warn("/stt returned non-OK", res.status);
        return;
      }

      const data = await res.json();
      // Expecting: { transcript: string, is_final: boolean }
      const transcript = (data.transcript || "").trim();
      const isFinal = !!data.is_final;

      if (!transcript) return;

      this.updateStreamingUser(transcript);

      const lower = transcript.toLowerCase();
      if (!this.state.awake && WAKE_WORDS.some((w) => lower.includes(w))) {
        this.wakeUpAssistant();
        this.streamingUserText = "";
        return;
      }

      if (this.state.awake) {
        this.resetSilenceTimer();
        if (isFinal) {
          this.handleQuery(transcript);
          this.streamingUserText = "";
        }
      }
    } catch (err) {
      console.warn("Error sending iOS audio chunk:", err);
    }
  }

  // -------------------- UI streaming helpers --------------------
  updateStreamingUser(text) {
    if (text == null) return;
    const conv = [...this.state.conversation];
    if (conv.length === 0 || conv[0].type !== "user" || conv[0].finalized) {
      const userEntry = { type: "user", text: text, finalized: false };
      const assistant = conv.find((m) => m.type === "assistant");
      this.setState({ conversation: assistant ? [userEntry, assistant] : [userEntry] });
      return;
    }
    conv[0].text = text;
    this.setState({ conversation: conv });
  }

  finalizeUserMessage() {
    const conv = [...this.state.conversation];
    if (conv.length && conv[0].type === "user") conv[0].finalized = true;
    this.setState({ conversation: conv });
  }

  // -------------------- Preload voices (only used when browser TTS available) --------------------
  async preloadVoices() {
    const loadVoices = () =>
      new Promise((resolve) => {
        const v = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
        if (v && v.length) resolve(v);
        else if (window.speechSynthesis)
          window.speechSynthesis.onvoiceschanged = () => resolve(window.speechSynthesis.getVoices());
        else resolve([]);
      });

    try {
      const voices = await loadVoices();
      const preferred = [
        "Google US English",
        "Microsoft Zira Desktop - English (United States)",
        "Microsoft Aria Online (Natural) - English (United States)",
        "Samantha",
        "Jenny",
      ];
      const voice = voices.find((v) => preferred.includes(v.name)) || voices.find((v) => v.lang && v.lang.startsWith("en")) || voices[0];
      if (voice) {
        this._preferredVoiceName = voice.name;
        localStorage.setItem("preferredFemaleVoice", voice.name);
      }
      this._voicesLoaded = true;
    } catch (err) {
      console.warn("Failed to preload voices:", err);
      this._voicesLoaded = false;
    }
  }

  // -------------------- Wake / Sleep --------------------
  wakeUpAssistant = () => {
    if (this.state.awake) return;
    this.setState({ awake: true }, () => {
      if (!this._voicesLoaded && this._firstUserGestureDone) this.preloadVoices();
      this.speak("Hi! I'm Parky. How can I help you today?", () => {
        this.startListening();
        this.startSilenceTimer();
      });
    });
  };

sleepAssistant = async () => {
  this.setState({ awake: false, listening: false });
  this.stopListening();
  this.clearSilenceTimer();
  this._clearShortSilenceTimer();
  this.activeBuffer = "";

  // Call backend to clear temporary chat history (LangChain + session)
  try {
    await fetch(`${BACKEND_URL}/sleep`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: this.sessionId }),
    });
    console.log("🧠 Session memory cleared on server.");
  } catch (err) {
    console.warn("Failed to clear session memory on server:", err);
  }

  // Speak confirmation after backend call
  this.speak("Going to sleep now.");
};

  // -------------------- Mic control (select implementation based on platform) --------------------
  startListening() {
    if (this.hasBrowserSTT) {
      if (!this.recognition) this.initSpeechRecognition();
      try {
        // reset internal buffer before starting listening
        this.activeBuffer = "";
        this._clearShortSilenceTimer();

        this.recognition.start();
        this.setState({ listening: true });
        console.log("🎤 Listening started (browser STT)");
      } catch (e) {
        console.warn("Recognition start failed:", e);
      }
      return;
    }

    if (this.isIOS) {
      // start MediaRecorder-based streaming to backend
      this.startIOSRecording();
      return;
    }

    console.warn("No suitable STT available on this platform.");
  }

  stopListening() {
    // Clear short silence buffer/timer when explicitly stopping listening
    this._clearShortSilenceTimer();

    if (this.hasBrowserSTT && this.recognition) {
      try {
        this.recognition.stop();
        this.setState({ listening: false });
      } catch (e) {}
      return;
    }

    if (this.isIOS) {
      this.stopIOSRecording();
      return;
    }
  }

  // -------------------- Silence timer --------------------
  startSilenceTimer() {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      console.log("😴 1 minute silence — sleeping...");
      this.sleepAssistant();
    }, 60000);
  }

  resetSilenceTimer() {
    this.startSilenceTimer();
  }

  clearSilenceTimer() {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
  }

  // -------------------- Short-silence helper (NEW) --------------------
  _resetShortSilenceTimer() {
    if (this.shortSilenceTimer) clearTimeout(this.shortSilenceTimer);

    // 2000ms silence triggers final send of buffer
    this.shortSilenceTimer = setTimeout(() => {
      const toSend = (this.activeBuffer || "").trim();
      if (!toSend) {
        this.activeBuffer = "";
        return;
      }
      this.activeBuffer = "";
      // mark user message finalized and send to backend (preserves current workflow)
      this.finalizeUserMessage();
      this.handleQuery(toSend);
    }, 2000);
  }

  _clearShortSilenceTimer() {
    if (this.shortSilenceTimer) {
      clearTimeout(this.shortSilenceTimer);
      this.shortSilenceTimer = null;
    }
  }

  // -------------------- Query handling --------------------
  async handleQuery(question) {
    if (!question.trim() || this.state.isProcessing) return;

    this.finalizeUserMessage();
    this.setState({
      conversation: [
        { type: "user", text: question, finalized: true },
        { type: "assistant", text: "" },
      ],
      isProcessing: true,
    });
    this.setState({ micLocked: true });
this.stopListening(); // ensure mic stays off


    const payload = {
      question,
      session_id: this.sessionId,
      meta: { garage: this.props?.garageData?.id || null },
      lang: this.state.currentLang,
    };

    try {
      const res = await fetch(`${BACKEND_URL}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      const fullReply = data.text?.trim() || "Sorry, I didn't understand that.";

      this.speak(fullReply, () => this.setState({ speaking: false }));

      this.streamResponseTyping(fullReply, () => {
        this.setState({ isProcessing: false });
        if (this.state.awake && !this.state.speaking) {
          try {
            if (this.hasBrowserSTT && this.recognition) this.recognition.start();
            if (this.isIOS && this.audioStream) this.startIOSRecording();
            this.setState({ listening: true });
          } catch {}
        }
        if (this.state.awake) this.startSilenceTimer();
      });
    } catch (err) {
      console.error("Backend error:", err);
      this.speak("Server error. Please try again later.");
      this.setState({ isProcessing: false });
    }
  }

  // -------------------- Fake streaming response (UI) --------------------
  streamResponseTyping(text, onComplete) {
    const threshold = 120;
    if (text.length <= threshold) {
      let i = 0;
      const step = () => {
        i++;
        const partial = text.substring(0, i);
        this.setState((prev) => {
          const conv = [...prev.conversation];
          const assistant = conv.find((m) => m.type === "assistant");
          if (assistant) assistant.text = partial;
          else conv.push({ type: "assistant", text: partial });
          return { conversation: conv };
        });
        if (i < text.length) setTimeout(step, 18 + Math.random() * 12);
        else if (onComplete) onComplete();
      };
      step();
      return;
    }

    const words = text.split(" ");
    let index = 0;
    let buffer = "";

    const pushChunk = () => {
      const chunkSize = Math.floor(Math.random() * 3) + 3;
      const nextWords = words.slice(index, index + chunkSize).join(" ");
      if (!nextWords) {
        if (onComplete) onComplete();
        return;
      }

      buffer += (buffer ? " " : "") + nextWords;
      index += chunkSize;

      this.setState((prev) => {
        const conv = [...prev.conversation];
        const last = conv[conv.length - 1];
        if (!last || last.type !== "assistant") {
          if (conv.length === 0) conv.push({ type: "assistant", text: buffer });
          else conv[1] = { type: "assistant", text: buffer };
        } else {
          last.text = buffer;
        }
        return { conversation: conv };
      });

      const nextDelay = 60 + Math.random() * 140;
      setTimeout(pushChunk, nextDelay);
    };

    pushChunk();
  }

  // -------------------- TTS: either browser or backend --------------------
  async speak(text, callback) {
    // If browser TTS available and not iOS -> use speechSynthesis
    if (this.hasBrowserTTS) return this._speakBrowser(text, callback);

    // otherwise use backend TTS
    return this._speakBackend(text, callback);
  }

  _speakBrowser(text, callback) {
    if (!window.speechSynthesis) {
      if (callback) callback();
      return;
    }

    try { window.speechSynthesis.cancel(); } catch {}
    this.stopListening();

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = this.state.currentLang;
    utter.pitch = 1.1;
    utter.rate = 1;

    const voices = window.speechSynthesis.getVoices();
    const preferred = this._preferredVoiceName || localStorage.getItem("preferredFemaleVoice");
    if (preferred) {
      const match = voices.find((v) => v.name === preferred);
      if (match) utter.voice = match;
    } else if (voices.length) {
      const fallback = voices.find((v) => /female|woman|girl|Samantha|Jenny|Zira|Aria/i.test(v.name)) || voices.find((v) => v.lang && v.lang.startsWith("en")) || voices[0];
      if (fallback) utter.voice = fallback;
    }

    utter.onstart = () => {
      this.setState({ speaking: true, listening: false });
      this.stopListening();
      this.resetSilenceTimer();
    };

    utter.onend = () => {
  this.setState({ speaking: false, micLocked: false }, () => { // unlock mic

        if (this.state.awake) {
          try {
            if (this.hasBrowserSTT && this.recognition) this.recognition.start();
            if (this.isIOS && this.audioStream) this.startIOSRecording();
            this.setState({ listening: true });
          } catch (e) { console.warn(e); }
          this.resetSilenceTimer();
        }
        if (callback) callback();
      });
    };

    try {
      window.speechSynthesis.speak(utter);
    } catch (err) {
      console.warn("speechSynthesis.speak failed:", err);
      if (callback) setTimeout(callback, 300);
    }
  }

  async _speakBackend(text, callback) {
    // POST JSON { text, lang, session_id } -> returns audio bytes (audio/mpeg or audio/wav)
    try {
      this.stopListening();
      this.setState({ speaking: true });

      const res = await fetch(`${BACKEND_URL}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, lang: this.state.currentLang, session_id: this.sessionId }),
      });

      if (!res.ok) {
        console.warn("/tts returned non-OK", res.status);
        this.setState({ speaking: false });
        if (callback) callback();
        return;
      }

      const audioBuffer = await res.arrayBuffer();
      const blob = new Blob([audioBuffer], { type: res.headers.get("Content-Type") || "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
       this.setState({ speaking: false, micLocked: false }, () => {

          if (this.state.awake) {
            try {
              if (this.hasBrowserSTT && this.recognition) this.recognition.start();
              if (this.isIOS && this.audioStream) this.startIOSRecording();
              this.setState({ listening: true });
            } catch (e) {}
            this.resetSilenceTimer();
          }
          if (callback) callback();
        });
      };
      audio.onerror = (e) => {
        console.warn("Playback error:", e);
        URL.revokeObjectURL(url);
        this.setState({ speaking: false });
        if (callback) callback();
      };
      audio.play().catch((e) => {
        console.warn("Audio play failed:", e);
        // user gesture may be required; mark speaking false and call callback
        this.setState({ speaking: false });
        if (callback) callback();
      });
    } catch (err) {
      console.error("_speakBackend error:", err);
      this.setState({ speaking: false });
      if (callback) callback();
    }
  }

  // -------------------- Language setter --------------------
  setLanguage(lang) {
    this.setState({ currentLang: lang }, () => {
      localStorage.setItem("preferredLang", lang);
      if (this.recognition) {
        try {
          this.recognition.stop();
          this.recognition.lang = lang;
          if (this.state.awake) this.recognition.start();
        } catch {}
      }
    });
  }

  componentWillUnmount() {
    this.clearSilenceTimer();
    this._clearShortSilenceTimer();
    this.stopListening();
    if (this.audioStream) {
      try { this.audioStream.getTracks().forEach(t => t.stop()); } catch {}
    }
  }

  // -------------------- Render --------------------
  render() {
    const { awake, speaking, listening, conversation, message } = this.state;

    return (
      <div className="container">
        <img src={logo} alt="Logo" className="top-left-logo" />
        <div className="header">
          <h1>🅿️ Parky</h1>
          <p>Your Smart Parking Assistant</p>
        </div>

        <div
          className={`wake-orb ${awake ? (speaking ? "speaking" : "awake") : "sleep"}`}
          onClick={() => (awake ? this.sleepAssistant() : this.wakeUpAssistant())}
          title={awake ? "Tap to sleep" : "Tap to wake Parky"}
        ></div>

        <div className="main-content portrait-layout">
          <div className="character-section">
            <img src={speaking ? animatedBotGif : realBotImage} alt="AI Assistant" className="character-image" />
            <div className="status-indicator">
              <div className="test-dot">
                <div className={`status-dot ${speaking ? "speaking" : awake ? "listening" : ""}`}></div>
                <span>
                  {awake ? (listening ? "Listening..." : "Processing...") : this._getWakeHint()}
                </span>
              </div>

              <div className="language-buttons">
                <p className="lang-title">🌐 Choose Language</p>
                <div className="lang-btn-row">
                  <button className={`lang-btn ${this.state.currentLang === "hi-IN" ? "active" : ""}`} onClick={() => this.setLanguage("hi-IN")}>Hindi</button>
                  <button className={`lang-btn ${this.state.currentLang === "en-US" ? "active" : ""}`} onClick={() => this.setLanguage("en-US")}>English</button>
                  <button className={`lang-btn ${this.state.currentLang === "es-ES" ? "active" : ""}`} onClick={() => this.setLanguage("es-ES")}>Spanish</button>
                </div>
              </div>
            </div>
          </div>

          <div className="conversation-section">
            <div className="conversation-header"><h3>💬 Conversation History</h3></div>
            <div className="conversation-log" ref={this.conversationRef}>
              {conversation.map((msg, i) => (
                <div key={i} className={`message ${msg.type}-message`}>
                  <div className="message-label">{msg.type === "user" ? "User:" : "Parky:"}</div>
                  <div className="message-text">{msg.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!awake && (
          <div className="intro-text"><p>{message}</p></div>
        )}
      </div>
    );
  }

  _getWakeHint() {
    if (this.isIOS) return "Tap the orb to wake Parky (iOS uses server STT/TTS)";
    return "Say 'Hey Parky' or tap the orb";
  }
}
